import { ComponentRef, Injectable } from '@angular/core';
import { AbcCore } from '@barthauer/abc/core';
import {
  AbcCoreComponent,
  AbcCoreContainerService,
  AbcPopupComponent,
  _AbcGridColumn,
  _AbcTreeNode,
} from '@barthauer/abc/ui-components';
import { tap } from 'rxjs/operators';
import { ActionButton, CategoryViewDefinition, UiSettings } from './models/protocol-viewer.model';
import { ProtocolViewerComponent } from './protocol-viewer.component';

@Injectable({
  providedIn: 'root',
})
export class ProtocolViewerService {
  private abcPopupComponent: AbcPopupComponent;
  private abcPopupContainer: AbcCoreComponent;
  public actionButtons: ActionButton[] = [];
  private keys: string[] = [];
  readonly viewModeButtons: ActionButton[] = [
    {
      caption: 'OK',
      action: async () => this.closeProtocolViewer(),
    },
  ];

  public categoryList: _AbcTreeNode<string>[] = [];
  public categoryViewDefinitions: Map<string | number, CategoryViewDefinition> = new Map();
  public categoryViewColumnInfos: Map<string | number, _AbcGridColumn[]> = new Map();
  public categoryDescriptionInfos: Map<string | number, string> = new Map();

  constructor(private abcCoreContainer: AbcCoreContainerService, private abcCore: AbcCore) {}

  public async openProtocolViewer(
    categoryDataSource: unknown,
    categoryViewColumnInfos?: Map<string | number, _AbcGridColumn[]>,
    categoryDescriptionInfos?: Map<string | number, string>,
    popUpTitle?: string,
    keys?: string[],
    action?: () => Promise<void>,
    disablehierarchy?: boolean
  ): Promise<void> {
    this.categoryList = [];
    this.categoryViewDefinitions.clear();
    this.categoryViewColumnInfos.clear();
    this.keys = [];

    if (keys) {
      this.keys = keys;
    }
    if (categoryViewColumnInfos) {
      this.categoryViewColumnInfos = categoryViewColumnInfos;
    }
    if (categoryDescriptionInfos) {
      this.categoryDescriptionInfos = categoryDescriptionInfos;
    }
    this.setActionButtons(action);
    this.buildCategoryStructure(categoryDataSource, undefined, disablehierarchy);

    this.abcPopupContainer = this.abcCoreContainer.createComponent(this.abcPopupContainer, AbcPopupComponent);
    this.abcPopupComponent = this.abcPopupContainer.componentRef.instance as AbcPopupComponent;
    this.abcPopupComponent.component = ProtocolViewerComponent;
    this.abcPopupComponent.resizeEnabled = true;
    this.abcPopupComponent.width = UiSettings.popupWidth;
    this.abcPopupComponent.height = UiSettings.popupHeight;
    this.abcPopupComponent.minWidth = UiSettings.popupMinWidth;
    this.abcPopupComponent.minHeight = UiSettings.popupMinHeight;
    this.abcPopupComponent.title = popUpTitle;
    this.abcPopupComponent.openPopUp() as ComponentRef<ProtocolViewerComponent>;
    try {
      await this.abcPopupComponent.onHiding
        .asObservable()
        .pipe(
          tap(async () => {
            this.closeProtocolViewer();
          })
        )
        .toPromise();
    } catch (err) {
      this.abcCore.error.publish(err);
    }
  }

  private setActionButtons(action?: () => Promise<void>): void {
    if (action) {
      this.actionButtons = [
        {
          caption: 'OK',
          action: async () => {
            await action();
            this.closeProtocolViewer();
          },
        },
      ];
    } else {
      this.actionButtons = this.viewModeButtons;
    }
  }

  public closeProtocolViewer(): void {
    if (this.abcPopupComponent) {
      this.abcPopupComponent.instance.dispose();
      this.abcPopupComponent.ngOnDestroy();
      this.abcPopupContainer.componentRef.destroy();
      this.abcPopupComponent = undefined;
      this.abcPopupContainer = undefined;
    }
    this.categoryDescriptionInfos = new Map();
  }

  private buildCategoryStructure(
    categoryDataSource: unknown,
    parentCategory?: _AbcTreeNode<string>,
    disableHierarchy?: boolean
  ): void {
    const categoryList: _AbcTreeNode<string>[] = [];
    const categoryKeys = Object.keys(categoryDataSource).filter(
      (item) => item !== 'Id' && item !== 'Infos' && item !== 'ObjectType' && item !== 'ManualSelection_'
    );
    for (let key of categoryKeys) {
      if (categoryDataSource[key].length) {
        const categoryListItem = new _AbcTreeNode<string>();
        categoryListItem.id = key;
        categoryListItem.icon = 'box';
        categoryListItem.caption = `${key + ' (' + categoryDataSource[key].length + ')'}`;
        categoryListItem.parentId = parentCategory?.id ?? null;

        const categoryViewDefinition = new CategoryViewDefinition();
        categoryViewDefinition.dataSource = categoryDataSource[key];
        categoryViewDefinition.columnInfos =
          this.categoryViewColumnInfos?.get(key) ?? this.buildCategoryViewColumnInfos(categoryDataSource[key][0]);

        this.categoryViewDefinitions.set(key, categoryViewDefinition);
        categoryList.push(categoryListItem);

        categoryDataSource[key].forEach((row: unknown, index: number) => {
          Object.keys(row).forEach((columnKey) => {
            if (Array.isArray(row[columnKey]) && !disableHierarchy) {
              this.buildCategoryStructure(
                { [`${columnKey} ${index}`]: row[columnKey] },
                categoryListItem,
                disableHierarchy
              );
              delete row[columnKey];
            }
          });
        });

        if (parentCategory && !disableHierarchy) {
          if (!parentCategory.children) {
            parentCategory.children = [];
          }
          parentCategory.children.push(categoryListItem);
        }
      }
    }

    if (!parentCategory) {
      this.categoryList = categoryList;
    }
  }

  private buildCategoryViewColumnInfos(categoryListItems: unknown): _AbcGridColumn[] {
    const columnInfos: _AbcGridColumn[] = [];

    const replacements: { [key: string]: string } = {
      'TypeDisplayText()': 'Object type',
      'Strasse.Name': 'Road',
      "Ortsteil.Concat(Name, Prefix(Ort.Name, ' / '))": 'Loc distr',
    };

    const keys = Object.keys(categoryListItems);
    let filteredKeys: string[];

    if (this.keys && this.keys.length > 0) {
      filteredKeys = keys.filter((key) => this.keys.some((word) => key.includes(word)));
      filteredKeys.forEach((key) => {
        const columnInfo = new _AbcGridColumn();
        const replacementKey = Object.keys(replacements).find((replacement) => key.includes(replacement));
        columnInfo.dataField = replacementKey ? key.replace(replacementKey, replacements[replacementKey]) : key;
        columnInfos.push(columnInfo);
      });
    } else {
      filteredKeys = keys.filter(
        (key) => key !== 'ObjectType' && key !== 'Id' && key !== 'Infos' && key !== 'ManualSelection_'
      );
      filteredKeys.forEach((key) => {
        const columnInfo = new _AbcGridColumn();
        columnInfo.dataField = key;
        columnInfos.push(columnInfo);
      });
    }

    return columnInfos;
  }
}
